/**
 * Get nonce for Caddy API requests
 *
 * @returns {string} Nonce value
 */
export function getCaddyNonce() {
	// Try to get nonce from meta tag first
	const nonceMeta = document.querySelector('meta[name="caddy-nonce"]');
	if (nonceMeta) {
		return nonceMeta.getAttribute('content');
	}

	// Fallback to global variable if available
	if (typeof window.caddyData !== 'undefined' && window.caddyData.nonce) {
		return window.caddyData.nonce;
	}

	// Final fallback - get from REST API nonce if available
	const restNonceMeta = document.querySelector('meta[name="wp-rest-nonce"]');
	if (restNonceMeta) {
		return restNonceMeta.getAttribute('content');
	}

	return '';
}

/**
 * Join an API root URL with a relative path.
 *
 * Plain-permalink safe: when the root is ?rest_route=/... a query string in
 * the path must join with & since the URL's ? is consumed by rest_route.
 *
 * @param {string} root API root URL
 * @param {string} path Path relative to the root
 * @returns {string} Absolute API URL
 */
function joinApiUrl(root, path) {
	const cleanPath = String(path).replace(/^\//, '');
	if (!cleanPath) {
		return root;
	}
	const base = root.endsWith('/') ? root : root + '/';
	if (base.includes('?') && cleanPath.includes('?')) {
		return base + cleanPath.replace('?', '&');
	}
	return base + cleanPath;
}

/**
 * Get the Caddy REST API URL for a given path.
 *
 * @param {string} path Optional path relative to the Caddy API root (e.g. 'saved-items/add')
 * @returns {string} Absolute Caddy REST API URL
 */
export function getCaddyRestUrl(path = '') {
	// Try to get REST URL from meta tag first
	const restUrlMeta = document.querySelector('meta[name="caddy-rest-url"]');
	const root = (restUrlMeta && restUrlMeta.getAttribute('content')) || (window.location.origin + '/wp-json/caddy/v1/');
	return joinApiUrl(root, path);
}

/**
 * Get the WooCommerce Store API URL for a given path.
 *
 * Resolved from the PHP-seeded meta tag (rest_url) so subdirectory installs
 * and plain-permalink sites work; falls back to the conventional path.
 *
 * @param {string} path Path relative to the Store API root (e.g. 'cart/add-item')
 * @returns {string} Absolute Store API URL
 */
export function getStoreApiUrl(path = '') {
	const meta = document.querySelector('meta[name="caddy-store-api-url"]');
	const root = (meta && meta.getAttribute('content')) || (window.location.origin + '/wp-json/wc/store/v1/');
	return joinApiUrl(root, path);
}

// Runtime fallback for pages where the nonce meta tag is absent
let runtimeStoreApiNonce = null;

/**
 * Get Store API nonce
 *
 * @returns {string|null} Store API nonce value
 */
export function getStoreApiNonce() {
	const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
	if (storeApiNonceMeta) {
		return storeApiNonceMeta.getAttribute('content');
	}
	return runtimeStoreApiNonce;
}

/**
 * Update Store API nonce from response headers.
 * WooCommerce returns a fresh Nonce header in Store API responses —
 * including error responses — which keeps us working on page-cached
 * sites where the meta tag nonce is frozen and eventually expires.
 *
 * @param {Response} response Fetch response object
 */
export function refreshNonceFromResponse(response) {
	const newNonce = response.headers.get('Nonce') || response.headers.get('X-WC-Store-API-Nonce');
	if (newNonce) {
		runtimeStoreApiNonce = newNonce;
		const meta = document.querySelector('meta[name="wc-store-api-nonce"]');
		if (meta) {
			meta.setAttribute('content', newNonce);
		}
	}
}

/**
 * Fetch wrapper for all WooCommerce Store API requests.
 *
 * - Resolves the URL via getStoreApiUrl() (subdirectory/plain-permalink safe)
 * - Attaches the current Store API nonce
 * - Harvests the fresh nonce from every response (page caches serve stale ones)
 * - Retries once with the refreshed nonce when the server rejects a stale nonce
 *
 * @param {string} path Path relative to the Store API root (e.g. 'cart/add-item')
 * @param {Object} options Fetch options (method, headers, body, ...)
 * @returns {Promise<Response>} Fetch response
 */
export async function storeApiFetch(path, options = {}) {
	const doFetch = () => {
		const headers = { ...(options.headers || {}) };
		const nonce = getStoreApiNonce();
		if (nonce) {
			headers['Nonce'] = nonce;
		}
		return fetch(getStoreApiUrl(path), {
			credentials: 'same-origin',
			...options,
			headers,
		});
	};

	let response = await doFetch();
	// Always harvest the fresh nonce — error responses carry one too
	refreshNonceFromResponse(response);

	// Stale nonce (e.g. frozen into a cached page): retry once with the fresh one
	if (response.status === 401 || response.status === 403) {
		let code = '';
		try {
			const body = await response.clone().json();
			code = (body && body.code) || '';
		} catch (e) {
			// Non-JSON error body — leave code empty, no retry
		}
		if (code === 'woocommerce_rest_invalid_nonce' || code === 'woocommerce_rest_missing_nonce') {
			response = await doFetch();
			refreshNonceFromResponse(response);
		}
	}

	return response;
}

/**
 * Check whether the PHP-seeded cart state matches the live cart.
 *
 * WooCommerce sets the `woocommerce_cart_hash` cookie whenever the cart is
 * non-empty (classic page loads AND Store API mutations) and clears it when
 * the cart empties. Cookies are never page-cached, so comparing the seeded
 * hash against the cookie detects a stale cached page without spending a
 * Store API request.
 *
 * @param {Object} state Interactivity API cart state seeded from PHP
 * @returns {boolean} True when the seeded state can be trusted as-is
 */
export function isSeededCartFresh(state) {
	let cookieHash = null;
	try {
		const match = document.cookie.match(/(?:^|;\s*)woocommerce_cart_hash=([^;]*)/);
		cookieHash = match ? decodeURIComponent(match[1]) : null;
	} catch (e) {
		return false;
	}
	const seededCount = parseInt(state && state.cartCount, 10) || 0;
	if (cookieHash) {
		// Live cart is non-empty: fresh only if rendered with the same hash
		return !!(state && state.cartHash) && state.cartHash === cookieHash;
	}
	// No cookie: live cart is empty — fresh only if the page was rendered empty
	// (seeded items + no cookie = stale cached page or post-checkout, e.g. #89)
	return seededCount === 0;
}

/**
 * Fetch cart data from WooCommerce Store API
 *
 * @returns {Promise<Object>} Cart data from Store API
 */
export async function fetchCart() {
	const response = await storeApiFetch('cart');

	if (!response.ok) {
		throw new Error('Failed to fetch cart');
	}

	return response.json();
}
